import _ from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ApplicationRecipientList} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {
  FindTeammateData,
  serializeRecipientForApplication,
} from '../../../helpers/models/applicationSerializationHelpers';
import {RecipientModel, RecipientRolesEnum} from '../../../models/api/recipientModel';
import {ThunkAction} from '../../appThunk';
import {ViewerPanelId} from '../../panels/panelStateBase';
import {makeGetViewerPanel} from '../../panels/viewer/viewerPanelSelectors';
import {ViewerPanelPageTypesEnum} from '../../panels/viewer/viewerPanelState';
import {getFlatChannels} from '../channels/channelsSelectors';
import {getCards, getContacts, getConversations, getCustomFields} from '../dataSelectors';
import {DataState} from '../dataState';
import {getFindTeammate, getFindTeammateByEmail} from '../teammates/lazyTeammatesSelectors';
import {getUser} from '../teammates/teammatesSelectors';
import {listLoadedMessageAction} from './applicationsMessagesLoadActions';

/*
 * Actions.
 */

export function applicationListConversationRecipientsAction(
  panelId: ViewerPanelId,
  conversationId: number,
): ThunkAction<ApplicationRecipientList> {
  return (dispatch, getState) => {
    const recipients = dispatch(listLoadedRecipientsAction(panelId, conversationId));
    const data = dispatch(getRecipientSerializationDataAction());
    const channels = getFlatChannels(getState());
    const conversation = getConversations(getState())[conversationId];
    const teammate = getUser(getState());
    const namespace = conversation?.namespace || teammate.namespace; // Default to the teammate's namespace.
    return {
      nextPageToken: undefined,
      results: recipients.map((r) => serializeRecipientForApplication(data, r, namespace, channels)),
    };
  };
}

export function listLoadedRecipientsAction(
  panelId: ViewerPanelId,
  conversationId: number,
): ThunkAction<ReadonlyArray<RecipientModel>> {
  return (dispatch, getState) => {
    const panel = makeGetViewerPanel()(getState(), {panelId});
    const viewerPage = panel?.pages.current;

    // Make sure the viewer page is loaded for the correct conversation.
    if (
      viewerPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ||
      viewerPage.conversationId !== conversationId ||
      viewerPage.status !== AsyncStatusesEnum.LOADED
    ) {
      return [];
    }

    const messages = dispatch(listLoadedMessageAction(panelId, conversationId));
    return _(messages)
      .sortBy((m) => m.sentAt)
      .reverse()
      .map((m) => m.recipients)
      .flatten()
      .uniqBy((rp) => rp.contactUid)
      .filter((rp) =>
        [
          RecipientRolesEnum.TO,
          RecipientRolesEnum.CC,
          RecipientRolesEnum.BCC,
          RecipientRolesEnum.FROM,
          RecipientRolesEnum.REPLY_TO,
        ].includes(rp.role),
      )
      .value();
  };
}

function getRecipientSerializationDataAction(): ThunkAction<
  Pick<DataState, 'contacts' | 'cards' | 'customFields'> & FindTeammateData
> {
  return (_dispatch, getState) => {
    const findTeammate = getFindTeammate(getState());
    const findTeammateByEmail = getFindTeammateByEmail(getState());
    const cards = getCards(getState());
    const contacts = getContacts(getState());
    const customFields = getCustomFields(getState());
    return {
      cards,
      contacts,
      customFields,
      findTeammate,
      findTeammateByEmail,
    };
  };
}
